/**
 * El mensaje que acompaña al QR cuando el residente lo comparte.
 *
 * Lo redactó el cliente, palabra por palabra, y por eso está aquí entero y no
 * repartido en el JSX: es un texto legal —identificación obligatoria, límites
 * de velocidad, corresponsabilidad del anfitrión— y quien lo cambie tiene que
 * poder leerlo de corrido para saber qué está cambiando.
 *
 * ## Por qué la fecha se arma a mano
 *
 * `toLocaleString('es-MX')` da un resultado distinto en cada sitio donde corre
 * esto: el navegador de escritorio, el WebView de Android y el de iOS traen
 * versiones de ICU diferentes, y unas ponen comas y «de» donde otras no. El
 * cliente aprobó un formato exacto —`vie 28 ago 2026 4:44 p. m.`— y con la API
 * del sistema no hay forma de garantizarlo. Con tablas propias sí, y además se
 * puede probar.
 */

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'] as const;
const MESES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
] as const;

/**
 * `vie 28 ago 2026 4:44 p. m.` — en la hora local de quien comparte.
 *
 * Local y no la del condominio: quien lee este mensaje es el visitante, y la
 * hora que le sirve es la del reloj que trae encima.
 */
export function fechaLarga(iso: string): string {
  const d = new Date(iso);
  const h24 = d.getHours();
  // 0 y 12 son los dos casos que se escapan de un módulo a secas: medianoche
  // se dice «12 a. m.» y mediodía «12 p. m.», no «0».
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const minutos = String(d.getMinutes()).padStart(2, '0');
  const franja = h24 < 12 ? 'a. m.' : 'p. m.';
  return (
    `${DIAS[d.getDay()]} ${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()} ` +
    `${h12}:${minutos} ${franja}`
  );
}

export interface DatosInvitacion {
  /** La vivienda que autoriza la visita, por su clave. */
  unidad: string;
  /** A quién se le emitió. Obligatorio desde que lo pidió el cliente. */
  nombreInvitado: string | null;
  /** Cuándo se creó la invitación, no cuándo vence. */
  creadoEn: string;
  /**
   * Los límites del reglamento interno, que cambian de un fraccionamiento a
   * otro. Estaban escritos aquí dentro hasta que el cliente pidió poder
   * cambiarlos; ahora salen de la ficha del condominio.
   */
  limiteVelocidadCamellon: number;
  limiteVelocidadCalles: number;
}

/**
 * El texto completo, listo para la hoja de compartir.
 *
 * **No menciona cuándo vence**, y no es un olvido: el cliente entregó este
 * texto cerrado. La vigencia sigue a la vista en la pantalla del residente,
 * que es quien decide cuánto dura.
 */
export function textoInvitacion(i: DatosInvitacion): string {
  // Las invitaciones anteriores a la regla del nombre obligatorio pueden no
  // traerlo. Se comparten igual: dejar al residente sin poder reenviar un
  // código que ya emitió sería peor que una línea genérica.
  const nombre = i.nombreInvitado?.trim() || 'el portador de este código';

  return [
    `Hola, esta es una invitación de visita autorizada a ${i.unidad}.`,
    `Fecha: ${fechaLarga(i.creadoEn)}`,
    `Menciona en la caseta: Soy visitante autorizado, mi nombre es: ${nombre}`,
    '',
    'El acceso será denegado sin identificación oficial. Recuerda que el límite ' +
      `de velocidad es de ${i.limiteVelocidadCamellon} km/hr, en calles con ` +
      `camellón y de ${i.limiteVelocidadCalles} km/hr, en calles y privadas, ` +
      'tu anfitrión es corresponsable de tus sanciones.',
  ].join('\n');
}
